import * as Sentry from "@sentry/remix";

export interface AuthTelemetryContext {
  ua: string;
  inAppBrowser: boolean;
  inAppBrowserName: string | null;
  platform: "ios" | "android" | "desktop";
  hasSessionCookie: boolean;
  hasSupabaseStorage: boolean;
  cookieEnabled: boolean;
}

const IN_APP_PATTERNS: Array<[RegExp, string]> = [
  [/Slack/i, "slack"],
  [/Discord/i, "discord"],
  [/FBAN|FBAV|FB_IAB/i, "facebook"],
  [/Instagram/i, "instagram"],
  [/LinkedInApp/i, "linkedin"],
  [/Line\//i, "line"],
  [/KAKAOTALK/i, "kakaotalk"],
  [/NAVER\(inapp/i, "naver"],
  [/Twitter/i, "twitter"],
  // Must precede the generic android-webview rule below.
  [/Telegram-iOS|TelegramX|Telegram\/\d/i, "telegram"],
  // Generic Android WebView — matches UAs that embed "; wv)".
  [/;\s*wv\)/i, "android-webview"],
];

export type InAppPlatform = AuthTelemetryContext["platform"];

export interface InAppBrowserDetection {
  isInApp: boolean;
  platform: InAppPlatform;
  appName: string | null;
}

export function detectInAppBrowser(): InAppBrowserDetection {
  if (typeof window === "undefined") {
    return { isInApp: false, platform: "desktop", appName: null };
  }
  const ua = navigator.userAgent;
  const match = IN_APP_PATTERNS.find(([pattern]) => pattern.test(ua));
  const platform: InAppPlatform = /iPhone|iPad|iPod/.test(ua)
    ? "ios"
    : /Android/.test(ua)
      ? "android"
      : "desktop";
  return { isInApp: !!match, platform, appName: match?.[1] ?? null };
}

export function getAuthContext(): AuthTelemetryContext {
  if (typeof window === "undefined") {
    return {
      ua: "",
      inAppBrowser: false,
      inAppBrowserName: null,
      platform: "desktop",
      hasSessionCookie: false,
      hasSupabaseStorage: false,
      cookieEnabled: false,
    };
  }

  const ua = navigator.userAgent;
  const detection = detectInAppBrowser();

  let hasSupabaseStorage = false;
  try {
    hasSupabaseStorage = Object.keys(localStorage).some((k) => k.startsWith("sb-"));
  } catch {
    // localStorage may be blocked in in-app browsers
  }

  return {
    ua,
    inAppBrowser: detection.isInApp,
    inAppBrowserName: detection.appName,
    platform: detection.platform,
    hasSessionCookie: document.cookie.includes("__session"),
    hasSupabaseStorage,
    cookieEnabled: navigator.cookieEnabled,
  };
}

export function captureAuthEvent(
  name: string,
  level: "info" | "warning" | "error" = "warning",
  extra?: Record<string, unknown>,
) {
  const ctx = getAuthContext();
  const path =
    typeof window !== "undefined" ? window.location.pathname : "";
  Sentry.captureMessage(name, {
    level,
    tags: {
      auth_event: name,
      in_app_browser: String(ctx.inAppBrowser),
      in_app_name: ctx.inAppBrowserName ?? "none",
      platform: ctx.platform,
    },
    extra: { ...ctx, path, ...extra },
  });
}

export function addAuthBreadcrumb(message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: "auth",
    message,
    level: "info",
    data: { ...getAuthContext(), ...data },
  });
}
